import { Api } from 'lib/api.mjs';
import { archiveEndpoint, archiveQuery } from 'lib/archive.mjs';

/**
 * The all-time archive, and the historical import that fills it.
 *
 * The paths come from lib/archive.mjs rather than being spelled again here —
 * campaigns and ads use different routes, and the detail route differs from the
 * search route, so there is one place that knows the shape.
 */

/**
 * Search the archive, or fetch one row's all-time history when `id` is given.
 *
 * @param {{ view?: 'campaigns'|'ads', id?: string, filters?: Object,
 *           signal?: AbortSignal }} payload
 */
export function searchArchive({ view = 'campaigns', id = '', filters, signal } = {}) {
  const path = id
    ? archiveEndpoint(view, id)
    : `${archiveEndpoint(view)}?${archiveQuery(filters ?? {})}`;
  // archiveEndpoint returns a full '/api/...' path; the transport adds its own
  // base, so strip the prefix rather than sending it twice.
  return Api.get(path.replace(/^\/api/, ''), { signal });
}

/**
 * The live Facebook/Instagram post behind ONE archived ad.
 *
 * Resolved by the server against Meta on demand — the archive stores figures,
 * not creatives — so this is called when an ad is opened, never for a list.
 * Always resolves to `{ postUrl }` with a string or null; null covers an ad
 * whose account has since been closed or banned as well as one that simply has
 * no underlying post, and both mean the same thing on screen: show no link.
 */
export function getAdPostUrl(adId, { signal } = {}) {
  return Api.get(`/archive/ads/${encodeURIComponent(adId)}/post`, { signal });
}

/**
 * Re-sync from the nightly snapshots. This DELETES rows with no spend, revenue
 * or purchases as well as writing, so callers confirm before calling it.
 */
export function rebuildArchive() {
  return Api.post('/archive/rebuild', {});
}

/**
 * Start a historical import. `accountIds` narrows it to one account; omitting it
 * imports every account, which is what the panel's "All accounts" means.
 *
 * @param {{ from: string, to?: string, accountIds?: string[] }} payload
 */
export function startImport({ from, to, accountIds }) {
  return Api.post('/archive/import', {
    body: { from, to: to || undefined, accountIds: accountIds?.length ? accountIds : undefined },
  });
}

export function getImportStatus({ signal } = {}) {
  return Api.get('/archive/import/status', { signal });
}

export function cancelImport() {
  return Api.post('/archive/import/cancel', {});
}
